import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { RouterOutlet, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { LanguageService } from './core/services/language.service';
import { SessionService } from './core/services/session.service';
import { selectIsAuthenticated } from './core/store/auth/auth.selectors';
import { AuthActions } from './core/store/auth/auth.actions';
import { SessionWarningComponent } from './shared/components/session-warning/session-warning.component';

/**
 * AppComponent
 * 
 * Root component of the application.
 * Este componente actua como contenedor principal para toda la aplicacion.
 */
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Crezco Foundation Admin';

  private readonly languageService = inject(LanguageService);
  private readonly sessionService = inject(SessionService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly document = inject(DOCUMENT);

  private readonly subs = new Subscription();
  private warningDialogOpen = false;

  constructor() {
    // Start/stop the session timer reactively based on auth state.
    effect(() => {
      if (this.store.selectSignal(selectIsAuthenticated)()) {
        this.sessionService.startSessionTimer();
      } else {
        this.sessionService.stopTimer();
      }
    });

    this.document.documentElement.setAttribute('data-color-mode', 'light');
    this.document.documentElement.setAttribute('data-theme', 'light');
  }

  ngOnInit(): void {
    this.store.dispatch(AuthActions.appInitializationRequested());
    this.languageService.initialize();

    this.subs.add(
      this.sessionService.sessionExpired$.subscribe(() => {
        this.store.dispatch(AuthActions.logoutRequested());
        this.router.navigate(['/auth/login'], {
          queryParams: { reason: 'session_expired' },
        });
      })
    );

    this.subs.add(
      this.sessionService.sessionWarning$.subscribe(() => {
        if (!this.warningDialogOpen) {
          this.warningDialogOpen = true;
          const ref = this.dialog.open(SessionWarningComponent, {
            disableClose: true,
            width: '380px',
          });
          ref.afterClosed().subscribe(() => {
            this.warningDialogOpen = false;
          });
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
